import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  ExternalLink,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Cpu,
  Swords,
  Trophy,
} from 'lucide-react';
import { SEO } from '@/components/seo';
import { FAQSchema, SoftwareApplicationSchema, OrganizationSchema } from '@/components/seo/Schema';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface DetectorStats {
  id: string;
  slug: string;
  name: string;
  url: string;
  logoUrl: string | null;
  accuracy: number | null;
  falsePositiveRate: number | null;
  falseNegativeRate: number | null;
  totalTested: number;
  eloRating: number;
  totalVotes: number;
  wins: number;
  losses: number;
  ties: number;
}

interface AIModelStats {
  id: string;
  slug: string;
  name: string;
  vendor: string;
  logoUrl: string | null;
  detectionRate: number | null;
  totalDetectors: number;
  detectedBy: number;
}

const FAQ_ITEMS = [
  {
    question: 'What is AI Detector Arena?',
    answer:
      'AI Detector Arena is an independent benchmark platform that tests AI image detectors against a curated dataset of AI-generated and real images. We measure accuracy, false positive rates, and false negative rates across multiple detectors and AI models. The Arena also features live Elo-based rankings where users vote on which detector performs better in head-to-head comparisons.',
  },
  {
    question: 'How does the Combined Score work?',
    answer:
      'The Combined Score merges two ranking systems: Benchmark Accuracy (60%) and Arena Elo (40%). Benchmark Accuracy comes from automated testing on our curated dataset. Arena Elo comes from community votes in head-to-head battles. This gives you a single number that reflects both objective testing and real-world comparative judgment.',
  },
  {
    question: 'How does the AI detector benchmark work?',
    answer:
      'We submit the same set of images — both AI-generated and real photographs — to each detector and record their verdicts. From these results we calculate accuracy (percentage of correct predictions), false positive rate (real images incorrectly flagged as AI), and false negative rate (AI images missed by the detector). All detectors are tested on the same dataset for a fair comparison.',
  },
  {
    question: 'What is the Arena Elo rating?',
    answer:
      'The Arena uses an Elo rating system — the same system used in chess rankings. Users are shown an image and two detector verdicts side by side, then vote for the detector that gave the better answer. Over time, detectors that consistently win accumulate higher Elo scores. This provides a complementary ranking to the static benchmark.',
  },
  {
    question: 'Which AI detectors are tested?',
    answer:
      'The benchmark includes major commercial and research AI detectors such as Hive Moderation, SightEngine, AI or Not, Illuminarty, Content at Scale, and others. We continuously add new detectors as they become available and re-test existing ones as they update their models.',
  },
  {
    question: 'Which AI image models are in the dataset?',
    answer:
      'Our dataset includes images from popular AI generators: Midjourney, Stable Diffusion (SDXL, SD 3.5), DALL-E 3, Flux, Adobe Firefly, Leonardo.ai, Runway, Google Imagen (Gemini), and Ideogram. The dataset also includes real photographs to test for false positives.',
  },
  {
    question: 'Is the benchmark independent?',
    answer:
      'Yes. AI Detector Arena is not affiliated with any AI detector vendor or AI image generator. We do not accept payment for rankings or favorable placement. All results are based on automated testing against our curated dataset.',
  },
];

// Calculate combined score using F1, FPR, FNR
// Formula: Score = 0.5 × F1 + 0.3 × (1 - FPR) + 0.2 × (1 - FNR)
// Where F1 = 2 × Precision × Recall / (Precision + Recall)
function calculateCombinedScore(
  accuracy: number | null,
  falsePositiveRate: number | null,
  falseNegativeRate: number | null
): number | null {
  if (accuracy === null || falsePositiveRate === null || falseNegativeRate === null) {
    return null;
  }

  // Convert percentages to decimals (0-1)
  const fpr = falsePositiveRate / 100;
  const fnr = falseNegativeRate / 100;

  // Calculate Precision and Recall from FPR and FNR
  // Recall = 1 - FNR (True Positive Rate)
  // For a balanced dataset: Precision ≈ (1 - FNR) / ((1 - FNR) + FPR)
  const recall = 1 - fnr;
  const precision = (recall + fpr) > 0 ? recall / (recall + fpr) : 0;

  // Calculate F1 score
  const f1 = (precision + recall) > 0
    ? (2 * precision * recall) / (precision + recall)
    : 0;

  // Calculate combined score (0-100 scale)
  const score = (0.5 * f1 + 0.3 * (1 - fpr) + 0.2 * (1 - fnr)) * 100;

  return Math.round(score * 100) / 100;
}

// Calculate F1 score from FPR and FNR
function calculateF1(falsePositiveRate: number | null, falseNegativeRate: number | null): number | null {
  if (falsePositiveRate === null || falseNegativeRate === null) {
    return null;
  }

  const fpr = falsePositiveRate / 100;
  const fnr = falseNegativeRate / 100;
  const recall = 1 - fnr;
  const precision = (recall + fpr) > 0 ? recall / (recall + fpr) : 0;

  if ((precision + recall) === 0) return null;

  return (2 * precision * recall) / (precision + recall) * 100;
}

export function BenchmarkPage() {
  const [detectors, setDetectors] = useState<DetectorStats[]>([]);
  const [models, setModels] = useState<AIModelStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [detectorsRes, modelsRes] = await Promise.all([
        fetch(`${API_URL}/benchmark/detectors`),
        fetch(`${API_URL}/benchmark/models`),
      ]);

      const detectorsData = await detectorsRes.json();
      const modelsData = await modelsRes.json();

      if (!detectorsRes.ok) throw new Error(detectorsData.error);
      if (!modelsRes.ok) throw new Error(modelsData.error);

      setDetectors(detectorsData.detectors || []);
      setModels(modelsData.models || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const MIN_BENCHMARK_TESTS = 50;

  // Filter detectors with enough benchmark tests, then sort by combined score
  const sortedDetectors = [...detectors]
    .filter((d) => d.totalTested >= MIN_BENCHMARK_TESTS)
    .sort((a, b) => {
      const aScore = calculateCombinedScore(a.accuracy, a.falsePositiveRate, a.falseNegativeRate);
      const bScore = calculateCombinedScore(b.accuracy, b.falsePositiveRate, b.falseNegativeRate);
      if (aScore === null) return 1;
      if (bScore === null) return -1;
      return bScore - aScore;
    });

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-500';
    if (score >= 90) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRateColor = (rate: number | null, threshold: number = 20) => {
    if (rate === null) return 'text-gray-500';
    if (rate <= 5) return 'text-green-400';
    if (rate <= threshold) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return <span className="text-2xl">🥇</span>;
    if (rank === 2) return <span className="text-2xl">🥈</span>;
    if (rank === 3) return <span className="text-2xl">🥉</span>;
    return <span className="text-gray-500 font-mono text-lg">{rank}</span>;
  };

  // Calculate stats
  const bestScore = sortedDetectors.length > 0
    ? calculateCombinedScore(
        sortedDetectors[0].accuracy,
        sortedDetectors[0].falsePositiveRate,
        sortedDetectors[0].falseNegativeRate
      )
    : null;

  return (
    <>
      <SEO
        title="Best AI Detectors 2026 - Accuracy Benchmark & Rankings | AI Detector Arena"
        description="Which AI image detector is most accurate? Compare 11 detectors by accuracy, false positive rate & ELO. Independent benchmark with 1000+ test images."
        canonical="/"
        keywords="best ai detectors, ai detector benchmark, ai image detector comparison, most accurate ai detector, ai detector accuracy, ai detector ranking"
      />
      <FAQSchema questions={FAQ_ITEMS} />
      <SoftwareApplicationSchema
        name="AI Detector Arena"
        description="Independent benchmark platform comparing AI image detectors. Test accuracy, false positive rates, and false negative rates across Hive, SightEngine, AI or Not, and more."
        applicationCategory="MultimediaApplication"
        operatingSystem="Web Browser"
        offers={{ price: '0', priceCurrency: 'USD' }}
      />
      <OrganizationSchema />
      <div className="min-h-screen bg-gray-950 text-white">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900/50">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-yellow-500" />
                  AI Detector Rankings
                </h1>
                <p className="text-gray-400 mt-2">
                  Combined benchmark accuracy & community arena ratings
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={loadData}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <Link
                  to="/arena"
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <Swords className="w-4 h-4" />
                  Vote in Arena
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">{detectors.length}</div>
              <div className="text-gray-500 text-sm">Detectors</div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">{models.length}</div>
              <div className="text-gray-500 text-sm">AI Models</div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-green-400">
                {bestScore !== null ? `${Math.round(bestScore)}` : '—'}
              </div>
              <div className="text-gray-500 text-sm">Top Score</div>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-center">
              {error}
              <button onClick={loadData} className="ml-2 underline">
                Try again
              </button>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          )}

          {/* Detector Rankings */}
          {!loading && detectors.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  Detector Rankings
                </h2>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800 bg-gray-800/50">
                      <th className="px-3 py-3 text-left text-sm font-medium text-gray-400 w-12">Rank</th>
                      <th className="px-3 py-3 text-left text-sm font-medium text-gray-400">Detector</th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400">Score</th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400 hidden md:table-cell">Accuracy</th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400 hidden md:table-cell">F1</th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400 hidden sm:table-cell">
                        <span className="hidden lg:inline">FPR</span>
                        <span className="lg:hidden">FPR</span>
                      </th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400 hidden sm:table-cell">
                        <span className="hidden lg:inline">FNR</span>
                        <span className="lg:hidden">FNR</span>
                      </th>
                      <th className="px-3 py-3 text-center text-sm font-medium text-gray-400 hidden lg:table-cell">Dataset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDetectors.map((detector, idx) => {
                      const combinedScore = calculateCombinedScore(
                        detector.accuracy,
                        detector.falsePositiveRate,
                        detector.falseNegativeRate
                      );
                      const f1Score = calculateF1(detector.falsePositiveRate, detector.falseNegativeRate);

                      return (
                        <tr
                          key={detector.id}
                          className={`border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors ${
                            idx < 3 ? 'bg-gray-800/20' : ''
                          }`}
                        >
                          <td className="px-4 py-4 text-center">
                            {getRankBadge(idx + 1)}
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-2">
                              {detector.logoUrl && (
                                <img
                                  src={detector.logoUrl}
                                  alt={`${detector.name} logo`}
                                  className="w-5 h-5 object-contain"
                                />
                              )}
                              <Link
                                to={`/detectors/${detector.slug}`}
                                className="font-medium hover:text-purple-400 transition-colors text-sm"
                              >
                                {detector.name}
                              </Link>
                              <a
                                href={detector.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-500 hover:text-gray-300"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-center">
                            <span className={`font-mono font-bold ${getScoreColor(combinedScore)}`}>
                              {combinedScore !== null ? combinedScore.toFixed(1) : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center hidden md:table-cell">
                            <span className={`font-mono ${getScoreColor(detector.accuracy)}`}>
                              {detector.accuracy !== null ? `${detector.accuracy.toFixed(1)}%` : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center hidden md:table-cell">
                            <span className={`font-mono ${getScoreColor(f1Score)}`}>
                              {f1Score !== null ? f1Score.toFixed(1) : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center hidden sm:table-cell">
                            <span className={`font-mono ${getRateColor(detector.falsePositiveRate)}`}>
                              {detector.falsePositiveRate !== null
                                ? `${detector.falsePositiveRate.toFixed(1)}%`
                                : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center hidden sm:table-cell">
                            <span className={`font-mono ${getRateColor(detector.falseNegativeRate)}`}>
                              {detector.falseNegativeRate !== null
                                ? `${detector.falseNegativeRate.toFixed(1)}%`
                                : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-center hidden lg:table-cell">
                            <span className="font-mono text-gray-400">
                              {detector.totalTested}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {/* AI Models Section */}
          {!loading && models.length > 0 && (
            <div className="mb-12">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-pink-500" />
                AI Model Detection Rates
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                How often each AI model gets detected. Lower = harder to detect.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {models
                  .filter(m => m.totalDetectors > 0)
                  .sort((a, b) => (a.detectionRate || 0) - (b.detectionRate || 0))
                  .map((model) => (
                    <Link
                      key={model.id}
                      to={`/models/${model.slug}`}
                      className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-purple-500/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          {model.logoUrl && (
                            <img src={model.logoUrl} alt={model.name} className="w-8 h-8 object-contain flex-shrink-0" />
                          )}
                          <div>
                            <div className="font-medium">{model.name}</div>
                            <div className="text-xs text-gray-500">{model.vendor}</div>
                          </div>
                        </div>
                        {model.detectionRate !== null && (
                          <div className={`flex items-center gap-1 ${
                            model.detectionRate >= 80 ? 'text-red-400' :
                            model.detectionRate >= 50 ? 'text-yellow-400' :
                            'text-green-400'
                          }`}>
                            {model.detectionRate >= 80 ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : model.detectionRate >= 50 ? (
                              <AlertTriangle className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                            <span className="font-mono font-bold">
                              {Math.round(model.detectionRate)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            (model.detectionRate || 0) >= 80 ? 'bg-red-500' :
                            (model.detectionRate || 0) >= 50 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${model.detectionRate || 0}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        Detected by {model.detectedBy}/{model.totalDetectors} detectors
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}

          {/* Methodology */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-12">
            <h2 className="text-xl font-semibold mb-4">How Rankings Work</h2>
            <div className="text-gray-400 text-sm space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Combined Score Formula</h3>
                <p>
                  The Combined Score balances three key metrics: <strong className="text-white">F1 Score (50%)</strong>,{' '}
                  <strong className="text-white">False Positive Rate (30%)</strong>, and{' '}
                  <strong className="text-white">False Negative Rate (20%)</strong>.
                </p>
                <p className="mt-2 font-mono text-xs bg-gray-800 p-2 rounded">
                  Score = 0.5 × F1 + 0.3 × (1 - FPR) + 0.2 × (1 - FNR)
                </p>
                <p className="mt-2">
                  This formula rewards detectors that balance precision (avoiding false positives) with recall (catching AI images).
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">F1 Score</h3>
                <p>
                  F1 is the harmonic mean of Precision and Recall. A high F1 means the detector is good at both
                  catching AI images (low FNR) and not flagging real images (low FPR). Formula: F1 = 2 × Precision × Recall / (Precision + Recall).
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">FPR & FNR</h3>
                <p>
                  <strong className="text-white">FPR (False Positive Rate)</strong> — percentage of real images incorrectly flagged as AI.
                  Lower is better. <strong className="text-white">FNR (False Negative Rate)</strong> — percentage of AI images missed.
                  Lower is better.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Dataset</h3>
                <p>
                  Our benchmark uses images from Midjourney, Stable Diffusion (SDXL, SD 3.5), DALL-E 3, Flux,
                  Adobe Firefly, Leonardo.ai, Runway, Google Imagen, and Ideogram. Real images are sourced from
                  photography databases to test for false positives.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-12">
            <h2 className="text-xl font-semibold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map((item, idx) => (
                <details
                  key={idx}
                  className="bg-gray-900 border border-gray-800 rounded-xl group"
                >
                  <summary className="px-6 py-4 cursor-pointer font-medium hover:text-purple-400 transition-colors list-none flex items-center justify-between">
                    {item.question}
                    <span className="text-gray-500 group-open:rotate-180 transition-transform ml-2">▾</span>
                  </summary>
                  <div className="px-6 pb-4 text-gray-400 text-sm">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BenchmarkPage;
